import React from "react";

/**
 * 文字列の判定に関する関数群
 */

/**
 * 文字列に日本語が含まれているか包括的に判定
 * @param {string} text - 判定対象の文字列
 * @returns {boolean} 日本語が含まれている場合はtrue
 */
export function containsJapanese(text) {
    return hasHiragana(text) || hasKatakana(text) || hasKanji(text) || hasHalfWidthKatakana(text);
}

/**
 * ひらがなが含まれているか判定
 * @param {string} text - 判定対象の文字列
 * @returns {boolean} ひらがなが含まれている場合はtrue
 */
export function hasHiragana(text) {
    return /[\u3040-\u309F]/.test(text);
}

/**
 * カタカナ（全角）が含まれているか判定
 * @param {string} text - 判定対象の文字列
 * @returns {boolean} カタカナが含まれている場合はtrue
 */
export function hasKatakana(text) {
    return /[\u30A0-\u30FF]/.test(text);
}

/**
 * 半角カタカナが含まれているか判定
 * @param {string} text - 判定対象の文字列
 * @returns {boolean} 半角カタカナが含まれている場合はtrue
 */
export function hasHalfWidthKatakana(text) {
    return /[\uFF65-\uFF9F]/.test(text);
}

/**
 * 漢字が含まれているか判定
 * @param {string} text - 判定対象の文字列
 * @returns {boolean} 漢字が含まれている場合はtrue
 */
export function hasKanji(text) {
    return /[\u4E00-\u9FFF]/.test(text);
}

/**
 * マルチバイト文字が含まれているか判定
 * @param {string} text - 判定対象の文字列
 * @returns {boolean} マルチバイト文字が含まれている場合はtrue
 */
export function hasMultibyteChar(text) {
    return /[^\x00-\xff]/.test(text);
}

/**
 * 文字がマルチバイト文字かどうかを判定
 * @param {string} char - 判定対象の文字
 * @returns {boolean} マルチバイト文字の場合はtrue
 */
export function isMultibyteChar(char) {
    return char.length === 1 && hasMultibyteChar(char);
}

/**
 * 文字列のバイト長を計算
 * @param {string} text - 計算対象の文字列
 * @returns {number} バイト長
 */
export function getByteLength(text) {
    let length = 0;
    for (let i = 0; i < text.length; i++) {
        length += isMultibyteChar(text[i]) ? 2 : 1;
    }
    return length;
}

/**
 * より詳細な文字種の分析を行う
 * @param {string} text - 分析対象の文字列
 * @returns {Object} 各文字種の検出結果と統計情報
 */
export function analyzeText(text) {
    const analysis = {
        hasJapanese: false,
        hasMultibyte: false,
        details: {
            hiragana: {
                present: false,
                count: 0,
                characters: []
            },
            katakana: {
                present: false,
                count: 0,
                characters: []
            },
            halfWidthKatakana: {
                present: false,
                count: 0,
                characters: []
            },
            kanji: {
                present: false,
                count: 0,
                characters: []
            },
            multibyte: {
                present: false,
                count: 0,
                characters: []
            }
        },
        composition: {
            total: {
                characters: text.length,
                bytes: getByteLength(text)
            },
            japanese: 0,
            multibyte: 0,
            ascii: 0
        }
    };

    // 文字ごとの分析
    for (const char of text) {
        const isMultibyte = isMultibyteChar(char);
        
        if (hasHiragana(char)) {
            analysis.details.hiragana.present = true;
            analysis.details.hiragana.count++;
            analysis.details.hiragana.characters.push(char);
            analysis.composition.japanese++;
        } else if (hasKatakana(char)) {
            analysis.details.katakana.present = true;
            analysis.details.katakana.count++;
            analysis.details.katakana.characters.push(char);
            analysis.composition.japanese++;
        } else if (hasHalfWidthKatakana(char)) {
            analysis.details.halfWidthKatakana.present = true;
            analysis.details.halfWidthKatakana.count++;
            analysis.details.halfWidthKatakana.characters.push(char);
            analysis.composition.japanese++;
        } else if (hasKanji(char)) {
            analysis.details.kanji.present = true;
            analysis.details.kanji.count++;
            analysis.details.kanji.characters.push(char);
            analysis.composition.japanese++;
        }
        
        if (isMultibyte) {
            analysis.details.multibyte.present = true;
            analysis.details.multibyte.count++;
            analysis.details.multibyte.characters.push(char);
            analysis.composition.multibyte++;
        } else {
            analysis.composition.ascii++;
        }
    }

    analysis.hasJapanese = analysis.composition.japanese > 0;
    analysis.hasMultibyte = analysis.composition.multibyte > 0;

    return analysis;
}

